import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  AssetType,
  CalendarEvent,
  statusConfig,
  assetTypeConfig,
} from './types';
import { ResourceType } from '@/types/club-resource.types';
import {
  useBlockUnavailabilityBerthMutation,
  useUnblockUnavailabilityBerthMutation,
  useCreateExternalReservationMutation,
  useUpdateExternalReservationMutation,
  useReleaseExternalReservationMutation,
} from '@/services/BerthManagementService';
import { useUpdateAddonMutation } from '@/services/AddonManagementService';
import { useSnackbar } from 'notistack';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

const getLocalDateString = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dayVal = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayVal}`;
};

interface EventModalProps {
  event: CalendarEvent | null;
  clickedDate?: Date;
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onView: () => void;
  canView?: boolean;
  cancelReason: string;
  setCancelReason: (v: string) => void;
  isSubmitting: boolean;
}

const EventModal: React.FC<EventModalProps> = ({
  event,
  clickedDate,
  isOpen,
  onClose,
  onCancel,
  onEdit,
  onView,
  canView = true,
  cancelReason,
  setCancelReason,
  isSubmitting,
}) => {
  const [isBlocking, setIsBlocking] = useState(false);
  const [isReservingExternally, setIsReservingExternally] = useState(false);
  const [isEditingExternally, setIsEditingExternally] = useState(false);
  const [blockStartDate, setBlockStartDate] = useState(event?.start || '');
  const [blockEndDate, setBlockEndDate] = useState(event?.end || '');
  const [blockReason, setBlockReason] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [blockSubmitting, setBlockSubmitting] = useState(false);
  const [externalReservationSubmitting, setExternalReservationSubmitting] =
    useState(false);
  const [isUnblockConfirmOpen, setIsUnblockConfirmOpen] = useState(false);
  const [unblockSubmitting, setUnblockSubmitting] = useState(false);
  const [isReleaseConfirmOpen, setIsReleaseConfirmOpen] = useState(false);
  const [releaseSubmitting, setReleaseSubmitting] = useState(false);
  const [blockUnavailabilityBerth] = useBlockUnavailabilityBerthMutation();
  const [unblockUnavailabilityBerth] = useUnblockUnavailabilityBerthMutation();
  const [createExternalReservation] = useCreateExternalReservationMutation();
  const [updateExternalReservation] = useUpdateExternalReservationMutation();
  const [releaseExternalReservation] = useReleaseExternalReservationMutation();
  const [updateAddon] = useUpdateAddonMutation();
  const { enqueueSnackbar } = useSnackbar();

  // Refs for accessibility
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Reset blocking state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsBlocking(false);
      setIsReservingExternally(false);
      setIsEditingExternally(false);
      setBlockReason('');
      setExternalReference('');
      setIsUnblockConfirmOpen(false);
      setUnblockSubmitting(false);
      setIsReleaseConfirmOpen(false);
      setReleaseSubmitting(false);
    }
  }, [isOpen]);

  // Reset block dates and reason whenever a new event is opened,
  // prefilling the start date with the date clicked on the calendar
  React.useEffect(() => {
    if (isOpen && event) {
      setBlockStartDate(
        (clickedDate ? getLocalDateString(clickedDate) : event.start) || ''
      );
      setBlockEndDate('');
      setBlockReason('');
      setExternalReference('');
      setIsBlocking(false);
      setIsReservingExternally(false);
      setIsEditingExternally(false);
    }
  }, [event, isOpen, clickedDate]);

  // Handle Escape key to close modal (WCAG)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Focus management and focus trap (WCAG)
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      // Auto-focus the close button when modal opens
      closeButtonRef.current.focus();

      // Focus trap: keep focus within modal
      const handleFocusTrap = (e: KeyboardEvent) => {
        if (e.key === 'Tab' && modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleFocusTrap);

      return () => {
        document.removeEventListener('keydown', handleFocusTrap);
      };
    }
  }, [isOpen]);

  if (!isOpen || !event) return null;

  const config = statusConfig[event.status] || {
    label: event.status || 'Unknown',
    color: 'bg-gray-500',
    lightColor: 'bg-gray-100 dark:bg-gray-200 contrast:bg-gray-900',
    textColor: 'text-gray-800 dark:text-gray-800 contrast:text-gray-100',
    borderColor: 'border-gray-500',
  };
  const assetType = event.assetType || (event.service ? 'service' : 'berth');
  const assetConfig = assetTypeConfig[assetType as AssetType] || {
    label: assetType || 'Unknown',
    color: 'bg-gray-500',
    lightColor: 'bg-gray-100 dark:bg-gray-200 contrast:bg-gray-900',
    textColor: 'text-gray-800 dark:text-gray-800 contrast:text-gray-100',
    borderColor: 'border-gray-500',
  };
  const isPortWatchShift =
    assetType === 'resource' &&
    event.resource?.resource_type === ResourceType.PORT_WATCH_SHIFTS;
  const showUnavailabilityDateInputs =
    assetType === 'berth' &&
    (((isBlocking || isReservingExternally) && !!event.isAvailable) ||
      (isEditingExternally && !!event.isExternallyReserved));
  const showExternalReservationForm =
    assetType === 'berth' &&
    ((isReservingExternally && !!event.isAvailable) ||
      (isEditingExternally && !!event.isExternallyReserved));

  const getOpenEndedTooltip = () => {
    if (event?.isExternallyReserved)
      return 'Reserved indefinitely (no end date set)';
    if (event?.isBlocked) return 'Blocked indefinitely (no end date set)';
    if (event?.isAvailable) return 'Availability shown without end date';
    return 'Ongoing booking (no end date set)';
  };

  const getTitleBasedOnAsset = () => {
    if (event.service) {
      return 'Service Booking Details';
    }
    if (event.isAvailable) {
      return assetType === 'berth'
        ? 'Available Berth'
        : `Available ${assetConfig.label}`;
    }
    if (event.isExternallyReserved) {
      return 'Reserved Berth';
    }
    if (event.isBlocked) {
      return assetType === 'berth'
        ? 'Blocked Berth'
        : `Blocked ${assetConfig.label}`;
    }
    return `${assetConfig.label} Booking Details`;
  };

  const handleBlockSave = async () => {
    if (!blockReason.trim()) {
      enqueueSnackbar('Please provide a reason for blocking the berth.', {
        variant: 'error',
      });
      return;
    }

    const cleanedId =
      event.berth?.id ||
      event.id.replace(/^available-/, '').replace(/-\d+$/, '');

    setBlockSubmitting(true);
    try {
      await blockUnavailabilityBerth({
        id: cleanedId,
        startDate: blockStartDate,
        endDate: blockEndDate || undefined,
        reason: blockReason,
      }).unwrap();
      enqueueSnackbar('Berth blocked successfully.', {
        variant: 'success',
      });
      setIsBlocking(false);
      setBlockReason('');
      onClose();
    } catch (error) {
      console.error('Error blocking berth:', error);
      const apiError = error as { data?: { message?: string } };
      enqueueSnackbar(
        apiError?.data?.message || 'Failed to block berth. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setBlockSubmitting(false);
    }
  };

  const handleUnblockConfirm = async () => {
    if (!event.berth?.id || !event.unavailability?.id) {
      enqueueSnackbar('Berth details not found for this blocked period.', {
        variant: 'error',
      });
      setIsUnblockConfirmOpen(false);
      return;
    }

    setUnblockSubmitting(true);
    try {
      await unblockUnavailabilityBerth({
        id: event.berth.id,
        unavailabilityId: event.unavailability.id as string,
      }).unwrap();
      enqueueSnackbar('Berth unblocked successfully.', {
        variant: 'success',
      });
      setIsUnblockConfirmOpen(false);
      onClose();
    } catch (error) {
      console.error('Error unblocking berth:', error);
      enqueueSnackbar('Failed to unblock berth. Please try again.', {
        variant: 'error',
      });
    } finally {
      setUnblockSubmitting(false);
    }
  };

  const handleExternalReservationSave = async () => {
    if (!blockReason.trim()) {
      enqueueSnackbar('Please provide a reason for the external reservation.', {
        variant: 'error',
      });
      return;
    }

    const cleanedId =
      event.berth?.id ||
      event.id.replace(/^available-/, '').replace(/-\d+$/, '');

    setExternalReservationSubmitting(true);
    try {
      await createExternalReservation({
        id: cleanedId,
        startDate: blockStartDate,
        endDate: blockEndDate || undefined,
        reason: blockReason,
        externalReference: externalReference || undefined,
      }).unwrap();
      enqueueSnackbar('Berth reserved successfully.', {
        variant: 'success',
      });
      setIsReservingExternally(false);
      setBlockReason('');
      setExternalReference('');
      onClose();
    } catch (error) {
      console.error('Error creating external reservation:', error);
      const apiError = error as { data?: { message?: string } };
      enqueueSnackbar(
        apiError?.data?.message ||
          'Failed to reserve the berth. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setExternalReservationSubmitting(false);
    }
  };

  const startEditingExternalReservation = () => {
    setBlockStartDate(event.start || '');
    setBlockEndDate(event.end || '');
    setBlockReason(event.unavailability?.reason || '');
    setExternalReference(event.unavailability?.external_reference || '');
    setIsReleaseConfirmOpen(false);
    setIsEditingExternally(true);
  };

  const cancelEditingExternalReservation = () => {
    setIsEditingExternally(false);
    setBlockReason('');
    setExternalReference('');
    setBlockStartDate(event.start || '');
    setBlockEndDate(event.end || '');
  };

  const handleExternalReservationUpdate = async () => {
    if (!blockReason.trim()) {
      enqueueSnackbar('Please provide a reason for the external reservation.', {
        variant: 'error',
      });
      return;
    }

    if (!event.berth?.id || !event.unavailability?.id) {
      enqueueSnackbar('Berth details not found for this reservation.', {
        variant: 'error',
      });
      return;
    }

    setExternalReservationSubmitting(true);
    try {
      await updateExternalReservation({
        id: event.berth.id,
        unavailabilityId: event.unavailability.id as string,
        startDate: blockStartDate,
        endDate: blockEndDate || undefined,
        reason: blockReason,
        externalReference: externalReference || undefined,
      }).unwrap();
      enqueueSnackbar('External reservation updated successfully.', {
        variant: 'success',
      });
      setIsEditingExternally(false);
      setBlockReason('');
      setExternalReference('');
      onClose();
    } catch (error) {
      console.error('Error updating external reservation:', error);
      const apiError = error as { data?: { message?: string } };
      enqueueSnackbar(
        apiError?.data?.message ||
          'Failed to update the reservation. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setExternalReservationSubmitting(false);
    }
  };

  const handleReleaseConfirm = async () => {
    if (!event.berth?.id || !event.unavailability?.id) {
      enqueueSnackbar('Berth details not found for this reservation.', {
        variant: 'error',
      });
      setIsReleaseConfirmOpen(false);
      return;
    }

    setReleaseSubmitting(true);
    try {
      await releaseExternalReservation({
        id: event.berth.id,
        unavailabilityId: event.unavailability.id as string,
      }).unwrap();
      enqueueSnackbar('External reservation released successfully.', {
        variant: 'success',
      });
      setIsReleaseConfirmOpen(false);
      onClose();
    } catch (error) {
      console.error('Error releasing external reservation:', error);
      const apiError = error as { data?: { message?: string } };
      enqueueSnackbar(
        apiError?.data?.message ||
          'Failed to release the reservation. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setReleaseSubmitting(false);
    }
  };

  const handleToggleAddonAvailability = async () => {
    if (!event.addon?.id) return;

    try {
      await updateAddon({
        addon_id: event.addon.id,
        data: {
          is_available: !event.addon.is_available,
        },
      }).unwrap();

      enqueueSnackbar(
        `Add-on marked as ${!event.addon.is_available ? 'available' : 'unavailable'} successfully`,
        { variant: 'success' }
      );

      onClose();
    } catch (error) {
      console.error('Error updating add-on availability:', error);
      enqueueSnackbar('Failed to update add-on availability', {
        variant: 'error',
      });
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999] p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="bg-gray-100 rounded-xl shadow-xl max-w-md w-full max-h-[500px] overflow-y-auto custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={0}
        aria-label="Event details"
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2
                id="modal-title"
                className="text-lg font-semibold text-gray-900"
              >
                {getTitleBasedOnAsset()}
              </h2>
              <div className="mt-1">
                <span
                  className={`inline-block px-1.5 py-0.5 text-xs font-medium ${assetConfig.lightColor} ${assetConfig.textColor} rounded`}
                >
                  {assetConfig.label}
                </span>
              </div>
            </div>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div
              className={`${config.lightColor} ${config.borderColor} border rounded-lg p-3 ${event.isAvailable || event.isBlocked || event.isExternallyReserved ? config.pattern || '' : ''}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-3 h-3 rounded-full ${config.color}`}></span>
                <span className={`text-sm font-medium ${config.textColor}`}>
                  {config.label}
                </span>
              </div>
              <h3 className="font-medium text-gray-900">{event.title}</h3>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {assetType === 'berth' && !event.service && (
                <div>
                  <span className="text-gray-500">Berth Number</span>
                  <p className="font-medium">{event.berthNumber}</p>
                </div>
              )}

              {event.service && (
                <>
                  <div className="col-span-2">
                    <span className="text-gray-500">Service Name</span>
                    <p className="font-medium">{event.service.name}</p>
                  </div>
                  {event.quantity && (
                    <div>
                      <span className="text-gray-500">Quantity</span>
                      <p className="font-medium">
                        {formatFinnishNumber(event.quantity, {
                          maximumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                  )}
                  {event.price !== undefined && (
                    <div>
                      <span className="text-gray-500">Total Price</span>
                      <p className="font-medium text-green-700 contrast:text-green-200">
                        {formatFinnishCurrency(event.price)}
                      </p>
                    </div>
                  )}
                  {event.startTime && event.endTime && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Time Slot</span>
                      <p className="font-medium">
                        {event.startTime} - {event.endTime}
                      </p>
                    </div>
                  )}
                </>
              )}

              {assetType === 'addon' && event.addon && (
                <>
                  <div className="col-span-2">
                    <span className="text-gray-500">Add-on Name</span>
                    <p className="font-medium">{event.addon.name}</p>
                  </div>
                  {event.addon.quantity && (
                    <div>
                      <span className="text-gray-500">Quantity</span>
                      <p className="font-medium">
                        {formatFinnishNumber(event.addon.quantity, {
                          maximumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                  )}
                  {event.addon.description && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Description</span>
                      <p className="font-medium text-sm">
                        {event.addon.description}
                      </p>
                    </div>
                  )}
                </>
              )}

              {!event.isAvailable &&
                !event.isBlocked &&
                !event.isExternallyReserved && (
                  <div>
                    <span className="text-gray-500">
                      {isPortWatchShift ? 'Assigned Member' : 'Guest'}
                    </span>
                    <p className="font-medium truncate">
                      {event.user.name}
                      {isPortWatchShift && event.user.email && (
                        <span className="text-sm text-gray-500">
                          {' '}
                          ({event.user.email})
                        </span>
                      )}
                    </p>
                  </div>
                )}

              {!event.service && (
                <>
                  <div>
                    {showUnavailabilityDateInputs ? (
                      <>
                        <label
                          htmlFor="block-start-date"
                          className="label-text font-medium block mb-1"
                        >
                          Start Date
                        </label>
                        <input
                          id="block-start-date"
                          type="date"
                          value={blockStartDate}
                          onChange={(e) => setBlockStartDate(e.target.value)}
                          autoComplete="off"
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm p-2"
                        />
                      </>
                    ) : (
                      <>
                        <span className="label-text font-medium">
                          Start Date
                        </span>
                        <p className="font-medium">
                          {formatDisplayDate(new Date(event.start))}
                        </p>
                      </>
                    )}
                  </div>

                  <div>
                    {showUnavailabilityDateInputs ? (
                      <>
                        <label
                          htmlFor="block-end-date"
                          className="text-gray-500 label-text font-medium block mb-1"
                        >
                          End Date
                        </label>
                        <input
                          id="block-end-date"
                          type="date"
                          value={blockEndDate}
                          onChange={(e) => setBlockEndDate(e.target.value)}
                          autoComplete="off"
                          min={blockStartDate}
                          className="w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm p-2"
                        />
                      </>
                    ) : (
                      <>
                        <span className="text-gray-500 label-text font-medium">
                          End Date
                        </span>
                        <p className="font-medium">
                          {event.end ? (
                            formatDisplayDate(new Date(event.end))
                          ) : (
                            <span
                              className="italic text-amber-600"
                              title={getOpenEndedTooltip()}
                              aria-label={getOpenEndedTooltip()}
                            >
                              Ongoing
                            </span>
                          )}
                        </p>
                      </>
                    )}
                  </div>
                  {isBlocking && event.isAvailable && assetType === 'berth' && (
                    <div className="col-span-2 text-sm">
                      <label
                        htmlFor="block-reason"
                        className="block text-gray-600 font-medium mb-1"
                      >
                        Block Reason
                      </label>
                      <textarea
                        id="block-reason"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="e.g. Maintenance, reserved, etc."
                        rows={3}
                        autoComplete="off"
                        className="w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm p-2 resize-y"
                      />
                      <p className="mt-1 text-[11px] text-gray-400 contrast:text-gray-600">
                        This will be stored with the berth block.
                      </p>
                    </div>
                  )}
                  {showExternalReservationForm && (
                      <>
                        <div className="col-span-2 text-sm">
                          <label
                            htmlFor="external-reservation-reason"
                            className="block text-gray-600 font-medium mb-1"
                          >
                            Reason
                          </label>
                          <textarea
                            id="external-reservation-reason"
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                            placeholder="e.g. Sold via broker contract #4521"
                            rows={3}
                            autoComplete="off"
                            className="w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm p-2 resize-y"
                          />
                          <p className="mt-1 text-[11px] text-gray-400 contrast:text-gray-600">
                            Required. This will be stored with the external
                            reservation.
                          </p>
                        </div>
                        <div className="col-span-2 text-sm">
                          <label
                            htmlFor="external-reference"
                            className="block text-gray-600 font-medium mb-1"
                          >
                            External Reference (optional)
                          </label>
                          <input
                            id="external-reference"
                            type="text"
                            value={externalReference}
                            onChange={(e) =>
                              setExternalReference(e.target.value)
                            }
                            maxLength={100}
                            placeholder="e.g. contract/invoice number"
                            autoComplete="off"
                            className="w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm p-2"
                          />
                        </div>
                      </>
                    )}
                </>
              )}

              {event.service && (
                <>
                  <div>
                    <span className="text-gray-500">Start Date</span>
                    <p className="font-medium">
                      {formatDisplayDate(new Date(event.start))}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">End Date</span>
                    <p className="font-medium">
                      {event.end ? formatDisplayDate(new Date(event.end)) : '-'}
                    </p>
                  </div>
                </>
              )}

              {assetType === 'addon' && event.addon?.price && (
                <div>
                  <span className="text-gray-500">Price</span>
                  <p className="font-medium">
                    <span className="text-green-700 contrast:text-green-200">
                      {formatFinnishCurrency(event.addon.price)}
                    </span>
                    <span className="text-gray-500 text-xs ml-1">
                      (
                      {event.addon.price_unit === 'PER_DAY'
                        ? 'Per Day'
                        : event.addon.price_unit === 'PER_HOUR'
                          ? 'Per Hour'
                          : event.addon.price_unit}
                      )
                    </span>
                  </p>
                </div>
              )}

              {assetType === 'addon' && (
                <div>
                  <span className="text-gray-500">Status</span>
                  <div className="flex items-center mt-1">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mr-1.5 ${event.addon?.is_available ? 'bg-green-500' : 'bg-red-500'}`}
                    ></div>
                    <span className="font-medium">
                      {event.addon?.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                </div>
              )}

              {(event.isBlocked || event.isExternallyReserved) &&
                assetType === 'berth' &&
                !isEditingExternally &&
                event.unavailability?.reason && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Reason</span>
                    <p className="font-medium text-sm italic text-gray-700">
                      {event.unavailability.reason}
                    </p>
                  </div>
                )}

              {event.isExternallyReserved &&
                assetType === 'berth' &&
                !isEditingExternally &&
                event.unavailability?.external_reference && (
                  <div className="col-span-2">
                    <span className="text-gray-500">External Reference</span>
                    <p className="font-medium text-sm">
                      {event.unavailability.external_reference}
                    </p>
                  </div>
                )}

              {event.isExternallyReserved &&
                assetType === 'berth' &&
                !isEditingExternally &&
                (event.unavailability?.created_by ||
                  event.unavailability?.updated_by) && (
                  <div className="col-span-2 text-xs text-gray-500">
                    {event.unavailability?.created_by && (
                      <p>
                        Created by{' '}
                        {event.unavailability.created_by.name ??
                          event.unavailability.created_by.email}
                      </p>
                    )}
                    {event.unavailability?.updated_by &&
                      event.unavailability?.updated_at && (
                        <p>
                          Last updated by{' '}
                          {event.unavailability.updated_by.name ??
                            event.unavailability.updated_by.email}{' '}
                          on{' '}
                          {formatDisplayDate(
                            new Date(event.unavailability.updated_at)
                          )}
                        </p>
                      )}
                  </div>
                )}
            </div>

            {event.isBlocked &&
              assetType === 'berth' &&
              isUnblockConfirmOpen && (
                <div className="mt-4 p-3 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900 contrast:bg-red-900 text-sm">
                  <p className="font-medium text-red-800 dark:text-red-200 contrast:text-red-200">
                    Unblock this berth?
                  </p>
                  <p className="text-red-700 text-xs mt-1 dark:text-red-300 contrast:text-red-300">
                    This will make the berth available again for new bookings.
                  </p>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setIsUnblockConfirmOpen(false)}
                      disabled={unblockSubmitting}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-900 bg-gray-100 hover:bg-gray-100 disabled:opacity-60"
                    >
                      No, keep blocked
                    </button>
                    <button
                      type="button"
                      onClick={handleUnblockConfirm}
                      disabled={unblockSubmitting}
                      className={`px-3 py-1.5 text-xs rounded-md text-white ${unblockSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'}`}
                    >
                      {unblockSubmitting ? 'Unblocking...' : 'Yes, Unblock'}
                    </button>
                  </div>
                </div>
              )}

            {event.isExternallyReserved &&
              assetType === 'berth' &&
              isReleaseConfirmOpen &&
              !isEditingExternally && (
                <div className="mt-4 p-3 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900 contrast:bg-amber-900 text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200 contrast:text-amber-200">
                    Release this reservation?
                  </p>
                  <p className="text-amber-700 text-xs mt-1 dark:text-amber-300 contrast:text-amber-300">
                    This will make the berth available again, subject to harbour
                    season and other restrictions.
                  </p>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setIsReleaseConfirmOpen(false)}
                      disabled={releaseSubmitting}
                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-900 bg-gray-100 hover:bg-gray-100 disabled:opacity-60"
                    >
                      No, keep reserved
                    </button>
                    <button
                      type="button"
                      onClick={handleReleaseConfirm}
                      disabled={releaseSubmitting}
                      className={`px-3 py-1.5 text-xs rounded-md text-white ${releaseSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'}`}
                    >
                      {releaseSubmitting ? 'Releasing...' : 'Yes, Release'}
                    </button>
                  </div>
                </div>
              )}

            {!event.isAvailable &&
              !event.isBlocked &&
              !event.isExternallyReserved &&
              event.status !== 'cancelled' &&
              event.status !== 'completed' && (
                <div className="text-sm">
                  <label
                    htmlFor="cancel-reason"
                    className="block text-gray-600 font-medium mb-1"
                  >
                    Cancellation Reason (optional)
                  </label>
                  <textarea
                    id="cancel-reason"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="e.g. Change of plans, bad weather, etc."
                    rows={3}
                    autoComplete="off"
                    className="w-full bg-gray-200 placeholder:text-gray-500 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm p-2 resize-y"
                  />
                  <p className="mt-1 text-[11px] text-gray-400 contrast:text-gray-600">
                    This will be stored with the booking cancellation.
                  </p>
                </div>
              )}
          </div>

          <div className="flex gap-3 mt-6">
            <div className="flex-1 flex flex-col gap-3">
              {event.isAvailable &&
                assetType === 'berth' &&
                !isBlocking &&
                !isReservingExternally && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setIsBlocking(true)}
                      className="flex-1 px-4 py-2 bg-red-600 contrast:bg-red-800 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Block
                    </button>
                    <button
                      onClick={() => setIsReservingExternally(true)}
                      className="flex-1 px-4 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors"
                    >
                      Reserve Externally
                    </button>
                  </div>
                )}
              {isEditingExternally &&
              event.isExternallyReserved &&
              assetType === 'berth' ? (
                <button
                  onClick={cancelEditingExternalReservation}
                  disabled={externalReservationSubmitting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              )}
            </div>
            {isBlocking && event.isAvailable && assetType === 'berth' && (
              <button
                onClick={handleBlockSave}
                disabled={blockSubmitting}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${blockSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'}`}
              >
                {blockSubmitting ? 'Saving...' : 'Save'}
              </button>
            )}
            {isReservingExternally &&
              event.isAvailable &&
              assetType === 'berth' && (
                <button
                  onClick={handleExternalReservationSave}
                  disabled={externalReservationSubmitting}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${externalReservationSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'}`}
                >
                  {externalReservationSubmitting ? 'Saving...' : 'Save'}
                </button>
              )}
            {isEditingExternally &&
              event.isExternallyReserved &&
              assetType === 'berth' && (
                <button
                  onClick={handleExternalReservationUpdate}
                  disabled={externalReservationSubmitting}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${externalReservationSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'}`}
                >
                  {externalReservationSubmitting ? 'Saving...' : 'Save'}
                </button>
              )}
            {event.isBlocked &&
              assetType === 'berth' &&
              !isUnblockConfirmOpen && (
                <button
                  onClick={() => setIsUnblockConfirmOpen(true)}
                  disabled={unblockSubmitting}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${unblockSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'}`}
                >
                  {unblockSubmitting ? 'Unblocking...' : 'Unblock'}
                </button>
              )}
            {event.isExternallyReserved &&
              assetType === 'berth' &&
              !isEditingExternally &&
              !isReleaseConfirmOpen && (
                <div className="flex flex-1 flex-col sm:flex-row gap-3">
                  <button
                    onClick={startEditingExternalReservation}
                    className="flex-1 px-4 py-2 bg-green-700 contrast:bg-green-800 text-white rounded-lg hover:bg-green-800 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setIsReleaseConfirmOpen(true)}
                    disabled={releaseSubmitting}
                    className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${releaseSubmitting ? 'bg-green-400 cursor-not-allowed' : 'bg-amber-700 hover:bg-amber-800'}`}
                  >
                    {releaseSubmitting ? 'Releasing...' : 'Release'}
                  </button>
                </div>
              )}
            {assetType === 'addon' &&
              event.addon &&
              (!event.addon.is_available ||
                (event.addon.is_available && event.isAvailable)) && (
                <button
                  onClick={handleToggleAddonAvailability}
                  disabled={isSubmitting}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${
                    isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : event.addon.is_available
                        ? 'bg-red-600 contrast:bg-red-800 hover:bg-red-700'
                        : 'bg-green-700 contrast:bg-green-800 hover:bg-green-800'
                  }`}
                >
                  {isSubmitting
                    ? 'Updating...'
                    : event.addon.is_available
                      ? 'Mark as Unavailable'
                      : 'Mark as Available'}
                </button>
              )}
            {!event.isAvailable &&
              !event.isBlocked &&
              !event.isExternallyReserved &&
              event.status !== 'cancelled' && (
                <>
                  {canView && (
                    <button
                      onClick={onView}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View
                    </button>
                  )}
                  {event.status !== 'completed' && (
                    <>
                      {(event.status === 'payment-pending' ||
                        event.status === 'pending-confirmation') && (
                        <button
                          onClick={onEdit}
                          className="flex-1 px-4 py-2 bg-green-700 contrast:bg-green-800 text-white rounded-lg hover:bg-green-800 transition-colors"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className={`flex-1 px-4 py-2 rounded-lg transition-colors text-white ${isSubmitting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 contrast:bg-red-800 hover:bg-red-700'}`}
                      >
                        {isSubmitting ? 'Cancelling...' : 'Cancel'}
                      </button>
                    </>
                  )}
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
